import { useEffect, useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';
import type { DesignerApplicationFormData } from '@/lib/validations/designer-application';

interface SectionFields {
  fields: (keyof DesignerApplicationFormData)[];
}

interface UseAutoScrollToNextSectionProps {
  sections: SectionFields[];
  watchedFields: Partial<DesignerApplicationFormData>;
  errors: FieldErrors<DesignerApplicationFormData>;
  enabled?: boolean;
}

/**
 * Custom hook that automatically scrolls to the next section
 * when the current section becomes complete (all fields valid).
 */
export function useAutoScrollToNextSection({
  sections,
  watchedFields,
  errors,
  enabled = true,
}: UseAutoScrollToNextSectionProps) {
  const previousCompletionStateRef = useRef<Record<number, boolean>>({});

  useEffect(() => {
    // Don't run if disabled (e.g., on mobile)
    if (!enabled) {
      return;
    }
    // Check if a field is valid (answered and has no errors)
    const isFieldValid = (fieldName: keyof DesignerApplicationFormData): boolean => {
      // If field has an error, it's not valid
      if (errors[fieldName]) {
        return false;
      }

      const value = watchedFields[fieldName];

      // Handle different field types
      if (Array.isArray(value)) {
        // For arrays (like languages, workSetup), consider valid if has at least one item
        return value.length > 0;
      }

      if (typeof value === 'boolean') {
        // For booleans, consider valid if true
        return value === true;
      }

      if (typeof value === 'string') {
        // For strings, consider valid if not empty (after trim)
        return value.trim().length > 0;
      }

      // For undefined/null, field is not valid
      if (value === undefined || value === null) {
        return false;
      }

      // For other types, check if value exists and is not empty string
      return value !== '';
    };

    // Check if a section is complete
    const isSectionComplete = (section: SectionFields, sectionIndex: number): boolean => {
      // Check all fields in the section are valid
      // For conditional fields like portfolioPasswordValue, handle them specially
      return section.fields.every((fieldName) => {
        // Special handling for portfolioPasswordValue - only required if portfolioPassword is "yes"
        if (fieldName === "portfolioPasswordValue") {
          if (watchedFields.portfolioPassword === "yes") {
            return isFieldValid(fieldName);
          }
          // If portfolioPassword is not "yes", portfolioPasswordValue is not required
          return true;
        }
        return isFieldValid(fieldName);
      });
    };

    // Track which sections just became complete
    const newlyCompleteSections: number[] = [];

    sections.forEach((section, index) => {
      const isCurrentlyComplete = isSectionComplete(section, index);
      const wasPreviouslyComplete = previousCompletionStateRef.current[index] || false;

      // If section transitioned from incomplete to complete, mark it as newly complete
      if (isCurrentlyComplete && !wasPreviouslyComplete) {
        newlyCompleteSections.push(index);
      }

      // Update previous state
      previousCompletionStateRef.current[index] = isCurrentlyComplete;
    });

    // If a section just became complete, scroll to next incomplete section
    if (newlyCompleteSections.length > 0) {
      // Find the first newly complete section
      const firstNewlyCompleteIndex = newlyCompleteSections[0];

      // Find the next incomplete section
      for (let i = firstNewlyCompleteIndex + 1; i < sections.length; i++) {
        const nextSection = sections[i];
        const isNextSectionComplete = isSectionComplete(nextSection, i);

        // If next section is not complete, scroll to it
        if (!isNextSectionComplete) {
          const element = document.querySelector(`[data-section-index="${i}"]`);
          if (element) {
            // Calculate scroll position with offset for sticky header
            const elementRect = element.getBoundingClientRect();
            const offset = 140; // Match scroll-padding-top from index.css
            const scrollPosition = window.pageYOffset + elementRect.top - offset;

            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth',
            });
          }
          break; // Only scroll to the first incomplete section
        }
      }
    }
  }, [sections, watchedFields, errors, enabled]);
}

